/**
 * Servicio de Google Places Autocomplete.
 *
 * Ruta doble:
 *   1. Places API (New) — google.maps.places.AutocompleteSuggestion con
 *      locationRestriction (bbox duro). Restricción REAL del lado del
 *      server. Requiere que el admin de Google Cloud haya habilitado
 *      "Places API (New)" en el proyecto.
 *   2. Legacy AutocompleteService — fallback cuando (a) la clase nueva
 *      no existe, (b) la API nueva no está habilitada (devuelve error),
 *      o (c) cualquier otro fallo. Aplica filtrado client-side
 *      post-hoc sobre las predictions como red de seguridad.
 *
 * En ambos caminos, si se pasa `allowedStates`, se post-filtra también
 * con extractPlaceState sobre la description por si Google devolvió
 * algún cross-border (la new API restringe por bbox, pero los bboxes
 * son rectangulares y pueden cubrir pedazos de estados vecinos).
 */

/**
 * Normaliza un string para matching sin acentos ni mayúsculas.
 */
function normalizeLoc(s) {
  if (!s) return '';
  return String(s)
    .toUpperCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/\s*\(.*?\)\s*/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Mapa de abreviaturas de estado mexicano → nombre canónico en mayúsculas.
 * Google Places usa abreviaturas en la description (penúltimo token antes
 * del país). Esta tabla permite expandir p.ej. "MEX." → "MEXICO", "CDMX"
 * → "CIUDAD DE MEXICO" para matchear contra el env del filtro.
 */
const PLACE_STATE_ABBREV = {
  'AGS': 'AGUASCALIENTES', 'AGS.': 'AGUASCALIENTES',
  'BC': 'BAJA CALIFORNIA', 'B.C.': 'BAJA CALIFORNIA',
  'BCS': 'BAJA CALIFORNIA SUR', 'B.C.S.': 'BAJA CALIFORNIA SUR',
  'CAMP': 'CAMPECHE', 'CAMP.': 'CAMPECHE',
  'CHIS': 'CHIAPAS', 'CHIS.': 'CHIAPAS',
  'CHIH': 'CHIHUAHUA', 'CHIH.': 'CHIHUAHUA',
  'CDMX': 'CIUDAD DE MEXICO', 'DF': 'CIUDAD DE MEXICO', 'D.F.': 'CIUDAD DE MEXICO',
  'COAH': 'COAHUILA', 'COAH.': 'COAHUILA',
  'COL': 'COLIMA', 'COL.': 'COLIMA',
  'DGO': 'DURANGO', 'DGO.': 'DURANGO',
  'GTO': 'GUANAJUATO', 'GTO.': 'GUANAJUATO',
  'GRO': 'GUERRERO', 'GRO.': 'GUERRERO',
  'HGO': 'HIDALGO', 'HGO.': 'HIDALGO',
  'JAL': 'JALISCO', 'JAL.': 'JALISCO',
  'MEX': 'MEXICO', 'MEX.': 'MEXICO', 'EDOMEX': 'MEXICO',
  'MICH': 'MICHOACAN', 'MICH.': 'MICHOACAN',
  'MOR': 'MORELOS', 'MOR.': 'MORELOS',
  'NAY': 'NAYARIT', 'NAY.': 'NAYARIT',
  'NL': 'NUEVO LEON', 'N.L.': 'NUEVO LEON',
  'OAX': 'OAXACA', 'OAX.': 'OAXACA',
  'PUE': 'PUEBLA', 'PUE.': 'PUEBLA',
  'QRO': 'QUERETARO', 'QRO.': 'QUERETARO',
  'QROO': 'QUINTANA ROO', 'Q. ROO': 'QUINTANA ROO', 'Q.ROO': 'QUINTANA ROO',
  'SLP': 'SAN LUIS POTOSI', 'S.L.P.': 'SAN LUIS POTOSI',
  'SIN': 'SINALOA', 'SIN.': 'SINALOA',
  'SON': 'SONORA', 'SON.': 'SONORA',
  'TAB': 'TABASCO', 'TAB.': 'TABASCO',
  'TAMS': 'TAMAULIPAS', 'TAMPS': 'TAMAULIPAS', 'TAMPS.': 'TAMAULIPAS',
  'TLAX': 'TLAXCALA', 'TLAX.': 'TLAXCALA',
  'VER': 'VERACRUZ', 'VER.': 'VERACRUZ',
  'YUC': 'YUCATAN', 'YUC.': 'YUCATAN',
  'ZAC': 'ZACATECAS', 'ZAC.': 'ZACATECAS',
};

/**
 * Bounding boxes aproximadas de los 32 estados mexicanos. Usadas para
 * construir el `locationRestriction` de la Places API (New) — restricción
 * DURA del lado del server. Valores en grados decimales (lat/lng).
 *   sw: [south, west]   ne: [north, east]
 */
const STATE_BBOX = {
  'AGUASCALIENTES':       { sw: [21.63, -102.87], ne: [22.45, -101.84] },
  'BAJA CALIFORNIA':      { sw: [28.00, -117.20], ne: [32.72, -112.76] },
  'BAJA CALIFORNIA SUR':  { sw: [22.90, -115.19], ne: [28.00, -109.41] },
  'CAMPECHE':             { sw: [17.81, -92.48],  ne: [20.85, -89.10] },
  'CHIAPAS':              { sw: [14.53, -94.14],  ne: [17.98, -90.37] },
  'CHIHUAHUA':            { sw: [25.56, -109.07], ne: [31.78, -103.30] },
  'CIUDAD DE MEXICO':     { sw: [19.05, -99.37],  ne: [19.59, -98.94] },
  'COAHUILA':             { sw: [24.54, -103.96], ne: [29.88, -99.84] },
  'COLIMA':               { sw: [18.23, -104.77], ne: [19.52, -103.44] },
  'DURANGO':              { sw: [22.31, -107.18], ne: [26.83, -102.47] },
  'GUANAJUATO':           { sw: [19.92, -102.08], ne: [21.85, -99.67] },
  'GUERRERO':             { sw: [16.31, -102.18], ne: [18.92, -98.00] },
  'HIDALGO':              { sw: [19.63, -99.91],  ne: [21.40, -97.96] },
  'JALISCO':              { sw: [18.92, -105.70], ne: [22.75, -101.49] },
  'MEXICO':               { sw: [18.35, -100.57], ne: [20.29, -98.35] },
  'MICHOACAN':            { sw: [17.91, -103.74], ne: [20.40, -100.06] },
  'MORELOS':              { sw: [18.33, -99.51],  ne: [19.12, -98.61] },
  'NAYARIT':              { sw: [20.61, -105.76], ne: [23.08, -103.72] },
  'NUEVO LEON':           { sw: [23.17, -101.22], ne: [27.82, -98.41] },
  'OAXACA':               { sw: [15.65, -98.53],  ne: [18.67, -93.87] },
  'PUEBLA':               { sw: [17.88, -99.07],  ne: [20.84, -96.72] },
  'QUERETARO':            { sw: [20.02, -100.58], ne: [21.67, -99.04] },
  'QUINTANA ROO':         { sw: [17.89, -89.24],  ne: [21.64, -86.71] },
  'SAN LUIS POTOSI':      { sw: [21.17, -102.30], ne: [24.54, -98.34] },
  'SINALOA':              { sw: [22.47, -109.45], ne: [27.03, -105.39] },
  'SONORA':               { sw: [26.28, -115.08], ne: [32.49, -108.40] },
  'TABASCO':              { sw: [17.25, -94.14],  ne: [18.65, -91.00] },
  'TAMAULIPAS':           { sw: [22.21, -100.15], ne: [27.68, -97.13] },
  'TLAXCALA':             { sw: [19.07, -98.70],  ne: [19.72, -97.62] },
  'VERACRUZ':             { sw: [17.14, -98.66],  ne: [22.46, -93.60] },
  'YUCATAN':              { sw: [19.58, -90.42],  ne: [21.60, -87.47] },
  'ZACATECAS':            { sw: [21.05, -104.35], ne: [25.14, -100.81] },
};

/**
 * Extrae el estado canónico de una description de Google Places.
 * Google típicamente estructura la description como:
 *   "Calle, Colonia, Ciudad, Estado-abrev, México"
 * Tomamos el penúltimo token (antes del país "México") y lo resolvemos
 * usando la tabla de abreviaturas. Si no está en la tabla, stripeamos
 * prefijos comunes ("Estado de ", "Edo. de ") para normalizar.
 */
function extractPlaceState(description) {
  if (!description) return '';
  const parts = String(description).split(',').map((s) => s.trim()).filter(Boolean);
  if (parts.length < 2) return '';
  const raw = parts[parts.length - 2];
  const norm = normalizeLoc(raw);
  if (PLACE_STATE_ABBREV[norm]) return PLACE_STATE_ABBREV[norm];
  return norm
    .replace(/^ESTADO DE\s+/, '')
    .replace(/^EDO\.?\s+DE\s+/, '')
    .trim();
}

/**
 * Union de bboxes para los estados dados. Si un estado no está en la
 * tabla STATE_BBOX, se ignora. Si ninguno matchea, devuelve null.
 * Formato de salida es el esperado por Places API (New) locationRestriction.
 */
function bboxUnion(stateNames) {
  const boxes = (stateNames || []).map((s) => STATE_BBOX[s]).filter(Boolean);
  if (!boxes.length) return null;
  let minLat = Infinity; let minLng = Infinity;
  let maxLat = -Infinity; let maxLng = -Infinity;
  for (const b of boxes) {
    minLat = Math.min(minLat, b.sw[0]);
    minLng = Math.min(minLng, b.sw[1]);
    maxLat = Math.max(maxLat, b.ne[0]);
    maxLng = Math.max(maxLng, b.ne[1]);
  }
  return {
    low:  { latitude: minLat, longitude: minLng },
    high: { latitude: maxLat, longitude: maxLng },
  };
}

/**
 * Servicio para interactuar con Google Places API
 * Limita las búsquedas al estado configurado en env (típicamente EdoMéx).
 */
class GooglePlacesService {
  constructor() {
    this.autocompleteService = null;
    this.placesService = null;
    this.initialized = false;
    // Cache feature-detect: si falla una vez, no intentar la new API otra
    // vez en esta sesión (evita spam de warnings).
    this._newApiDisabled = false;
  }

  /**
   * Inicializa los servicios de Google Places (legacy).
   */
  initialize() {
    if (this.initialized) return;
    if (!window.google || !window.google.maps || !window.google.maps.places) {
      console.warn('GooglePlacesService: Google Maps Places API not loaded');
      return;
    }
    this.autocompleteService = new window.google.maps.places.AutocompleteService();
    this.placesService = new window.google.maps.places.PlacesService(
      document.createElement('div'),
    );
    this.initialized = true;
  }

  /**
   * Feature detect de la Places API (New).
   */
  _hasNewApi() {
    if (this._newApiDisabled) return false;
    return !!(
      window.google && window.google.maps && window.google.maps.places
      && typeof window.google.maps.places.AutocompleteSuggestion !== 'undefined'
      && typeof window.google.maps.places.AutocompleteSuggestion
        .fetchAutocompleteSuggestions === 'function'
    );
  }

  /**
   * Bbox de fallback del Estado de México (legacy LatLngBounds).
   */
  getEstadoMexicoBounds() {
    return new window.google.maps.LatLngBounds(
      new window.google.maps.LatLng(19.0, -100.0),
      new window.google.maps.LatLng(20.5, -98.5),
    );
  }

  /**
   * Busca lugares usando Google Places Autocomplete.
   * Intenta primero la Places API (New); si no está disponible o falla,
   * cae al legacy AutocompleteService.
   *
   * @param {string} query
   * @param {number} minLength
   * @param {number} maxResults
   * @param {string[]} [allowedStates] - Estados canónicos a permitir.
   * @returns {Promise<Array>}
   */
  async searchPlaces(query, minLength = 5, maxResults = 5, allowedStates) {
    if (!this.initialized) this.initialize();
    if (!query || query.length <= minLength) return [];

    const hasFilter = Array.isArray(allowedStates) && allowedStates.length > 0;
    const normAllowed = hasFilter ? allowedStates.map(normalizeLoc).filter(Boolean) : [];

    // Intento 1: Places API (New) con locationRestriction DURO.
    if (this._hasNewApi()) {
      try {
        const res = await this._searchPlacesNew(query, maxResults, normAllowed);
        if (res !== null) return res;
      } catch (err) {
        // Si la API no está habilitada, Google devuelve un error 403 o
        // un rechazo explícito. Lo logueamos UNA vez y marcamos
        // _newApiDisabled para no reintentar el resto de la sesión.
        console.warn('[GooglePlaces] New API falló, cayendo a legacy:', err && (err.message || err));
        this._newApiDisabled = true;
      }
    }

    // Intento 2 (fallback): Legacy AutocompleteService.
    return this._searchPlacesLegacy(query, maxResults, normAllowed);
  }

  /**
   * Ruta 1: Places API (New). Usa AutocompleteSuggestion con
   * locationRestriction (bbox duro). Si la API no está habilitada
   * en el proyecto, Google lanza un error y caemos al legacy.
   */
  async _searchPlacesNew(query, maxResults, normAllowed) {
    const places = window.google.maps.places;
    const request = {
      input: query,
      includedRegionCodes: ['mx'],
      language: 'es-MX',
      region: 'mx',
    };
    // Restricción dura por bbox cuando hay estados configurados.
    const bbox = bboxUnion(normAllowed);
    if (bbox) request.locationRestriction = { rectangle: bbox };

    const { suggestions } = await places.AutocompleteSuggestion
      .fetchAutocompleteSuggestions(request);

    const hasFilter = normAllowed.length > 0;
    const items = [];
    for (let i = 0; i < suggestions.length && items.length < maxResults; i++) {
      const pred = suggestions[i].placePrediction;
      if (!pred) continue; // ignoramos queryPredictions, solo lugares

      const description = (pred.text && pred.text.text) || '';

      // Safety net: el bbox es rectangular y puede incluir pedazos de
      // estados vecinos; el extractor por texto limpia esos edge cases.
      if (hasFilter) {
        const state = extractPlaceState(description);
        if (!state || normAllowed.indexOf(state) === -1) continue;
      }

      items.push({
        name: description,
        typeOfItem: 'place',
        placeId: pred.placeId,
      });
    }
    return items;
  }

  /**
   * Ruta 2 (fallback): Legacy AutocompleteService con bbox bias + post-filter.
   */
  _searchPlacesLegacy(query, maxResults, normAllowed) {
    if (!this.autocompleteService) return Promise.resolve([]);
    const hasFilter = normAllowed.length > 0;

    return new Promise((resolve) => {
      const bounds = this.getEstadoMexicoBounds();
      this.autocompleteService.getPlacePredictions(
        {
          input: query,
          componentRestrictions: { country: 'mx' },
          bounds,
        },
        (predictions, status) => {
          if (
            status === window.google.maps.places.PlacesServiceStatus.OK
            && predictions
          ) {
            const filtered = hasFilter
              ? predictions.filter((p) => {
                const state = extractPlaceState(p.description || '');
                return !!state && normAllowed.indexOf(state) !== -1;
              })
              : predictions;
            const limited = filtered.slice(0, maxResults).map((p) => ({
              name: p.description,
              typeOfItem: 'place',
              placeId: p.place_id,
            }));
            resolve(limited);
          } else {
            resolve([]);
          }
        },
      );
    });
  }

  /**
   * Obtiene los detalles completos de un lugar por su placeId.
   * (Sigue usando el legacy PlacesService — la migración del details
   * a la new Place class queda para un futuro, no bloquea el filtro.)
   */
  async getPlaceDetails(placeId, fields = ['address_components', 'geometry', 'icon', 'name']) {
    if (!this.initialized) this.initialize();
    if (!this.placesService || !placeId) return null;

    return new Promise((resolve) => {
      this.placesService.getDetails(
        { placeId, fields },
        (place, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
            resolve(place);
          } else {
            resolve(null);
          }
        },
      );
    });
  }
}

// Exportar instancia singleton
export default new GooglePlacesService();
